import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Avatar } from "./Avatar";
import { clearAvatar, uploadAvatar } from "../lib/uploadAvatar";
import type { MeResponse } from "@family/shared";

interface Props {
  me: MeResponse;
  onUpdated: (next: MeResponse) => void;
}

/**
 * Avatar picker for the Profile page.
 *
 * - One file input opens the OS picker; on iOS Safari that natively offers
 *   "Take Photo" / "Photo Library" / "Choose File" without us doing anything
 *   special, as long as the input has accept="image/*".
 * - After a successful upload, if the user is linked to a person in the
 *   family tree AND that person has no cover photo yet, we ask whether to
 *   reuse the avatar there. If the linked person already has a cover, we
 *   leave it alone — they've explicitly placed something in the tree and
 *   we don't want to clobber it.
 */
export function AvatarUploader({ me, onUpdated }: Props) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const upload = useMutation<MeResponse, Error, { file: File; alsoUseOnTree: boolean }>({
    mutationFn: ({ file, alsoUseOnTree }) => uploadAvatar(file, { alsoUseOnTree }),
    onSuccess: (next) => {
      queryClient.setQueryData<MeResponse>(["me"], next);
      onUpdated(next);
      setError(null);
      setPendingFile(null);
    },
    onError: (err) => {
      setError(err.message);
      setPendingFile(null);
    },
  });

  const remove = useMutation<void, Error, void>({
    mutationFn: () => clearAvatar(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  function pick() {
    fileRef.current?.click();
  }

  function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-picking the same file fires onChange again
    if (!file) return;

    // Decide whether we need to ask the "use on tree?" question.
    const linkedToPerson = Boolean(me.user.linkedPersonId);
    const personAlreadyHasCover = me.user.linkedPersonHasCover;

    if (linkedToPerson && !personAlreadyHasCover) {
      // Stash the file and present the prompt; the user picks yes/no.
      setPendingFile(file);
      return;
    }

    // No prompt needed — straight upload, never push to tree.
    upload.mutate({ file, alsoUseOnTree: false });
  }

  function answerTreePrompt(useOnTree: boolean) {
    if (!pendingFile) return;
    upload.mutate({ file: pendingFile, alsoUseOnTree: useOnTree });
  }

  const displayName =
    [me.user.firstName, me.user.lastName].filter(Boolean).join(" ") ||
    me.user.displayName ||
    null;

  return (
    <div className="stack" style={{ maxWidth: 560 }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--s-4)" }}>
        <Avatar src={me.user.avatarUrl} name={displayName} size={80} />

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button
            type="button"
            onClick={pick}
            disabled={upload.isPending}
            style={{ minHeight: 36, padding: "6px 14px", fontSize: "0.9rem" }}
          >
            {upload.isPending
              ? "Uploading…"
              : me.user.avatarUrl
                ? "Replace photo"
                : "Add photo"}
          </button>
          {me.user.avatarUrl && (
            <button
              type="button"
              className="btn-tertiary"
              onClick={() => {
                if (window.confirm("Remove your photo?")) remove.mutate();
              }}
              disabled={remove.isPending}
              style={{
                minHeight: 32,
                padding: "4px 12px",
                fontSize: "0.85rem",
                color: "var(--c-danger)",
              }}
            >
              Remove
            </button>
          )}
          <p className="text-subtle" style={{ fontSize: "0.8rem", margin: 0 }}>
            JPEG, PNG, or WebP. Up to 2 MB.
          </p>
        </div>

        {/* Hidden input. No `capture` attribute: with it set, Android Chrome
            (and current iOS Safari) jump straight to the camera with no
            library option. Without it, mobile browsers offer both
            "Take Photo" and "Photo Library". */}
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={onFileChosen}
          style={{ display: "none" }}
        />
      </div>

      {error && <p className="text-error">{error}</p>}

      {/* Tree-cover prompt. Modal-feel without the JS overhead of a real
          dialog — just a card we render inline above the form. */}
      {pendingFile && (
        <div className="card stack" style={{ borderColor: "var(--c-blue)" }}>
          <strong>Use this photo on the family tree too?</strong>
          <p className="text-muted" style={{ fontSize: "0.9rem", margin: 0 }}>
            We can also set this as your photo on the family tree, since you
            don't have one there yet.
          </p>
          <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => answerTreePrompt(true)}
              disabled={upload.isPending}
              style={{ minHeight: 34, padding: "6px 14px", fontSize: "0.9rem" }}
            >
              {upload.isPending ? "Uploading…" : "Yes, use on tree"}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => answerTreePrompt(false)}
              disabled={upload.isPending}
              style={{ minHeight: 34, padding: "6px 14px", fontSize: "0.9rem" }}
            >
              No, just my profile
            </button>
            <button
              type="button"
              className="btn-tertiary"
              onClick={() => setPendingFile(null)}
              disabled={upload.isPending}
              style={{ minHeight: 34, padding: "6px 14px", fontSize: "0.9rem" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
